import { useState } from 'react'
import Box from '@mui/material/Box'
import Popover from '@mui/material/Popover'
import CircularProgress from '@mui/material/CircularProgress'
import Tooltip from 'components/design-system/Tooltip'
import NavItem from 'components/design-system/NavItem'
import Icon from 'components/design-system/Icon'
import IconButton from 'components/design-system/IconButton'
import EmptyState from 'components/design-system/EmptyState'
import badgeDot from 'assets/images/icons/nav-badge-dot.svg'
import { useDownload } from 'hooks/useDownload'

const statusIcon = (status) => {
  if (status === 'pending') return <CircularProgress size={14} sx={{ color: 'var(--color-icon-accent)' }} aria-label="Downloading" />
  if (status === 'error') return <Icon name="cancel" size={16} color="var(--color-icon-danger)" aria-label="Failed" />
  return <Icon name="check" size={16} color="var(--color-status-success-fg)" aria-label="Done" />
}

const textSx = { fontFamily: 'var(--font-family-primary)', lineHeight: 'normal', color: 'var(--color-text-primary)' }

export default function NotificationPopover() {
  const [anchorEl, setAnchorEl] = useState(null)
  const { tasks, removeTaskById } = useDownload()

  const open = Boolean(anchorEl)
  const id = open ? 'notification-popover' : undefined

  return (
    <>
      <Box sx={{ position: 'relative', flexShrink: 0, width: 32, height: 32 }}>
        <Tooltip title="Downloads" placement="right">
          <NavItem icon="notifications" aria-describedby={id} aria-label="Downloads" onClick={(e) => setAnchorEl(e.currentTarget)} />
        </Tooltip>
        {tasks.some((t) => t.status === 'pending') && (
          <Box
            component="img"
            src={badgeDot}
            alt=""
            aria-hidden
            sx={{ position: 'absolute', left: 18, top: 2, width: 12, height: 12, pointerEvents: 'none' }}
          />
        )}
      </Box>

      {/* Downloads popover: cùng kiểu surface với Account PopUp (Gradient/Surface, border/default, radius/md, Shadow/Card) */}
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--space-12)',
              boxSizing: 'border-box',
              width: 300,
              ml: 'var(--space-8)',
              p: 'var(--space-16)',
              backgroundColor: 'transparent',
              backgroundImage: 'linear-gradient(to bottom, var(--color-navy-850), var(--color-navy-950))', // Gradient/Surface
              border: 'var(--stroke-default) solid var(--color-border-default)',
              borderRadius: 'var(--radius-md)',
              boxShadow: '4px 4px 16px 0 rgba(0, 0, 0, 0.3)' // Shadow/Card
            }
          }
        }}
      >
        <Box component="h2" sx={{ ...textSx, m: 0, fontSize: 'var(--font-size-16)', fontWeight: 'var(--font-weight-bold)' }}>
          Downloads
        </Box>

        {tasks.length === 0 ? (
          <EmptyState icon="download" message="No downloads" sx={{ py: 'var(--space-8)' }} />
        ) : (
          <Box
            component="ul"
            sx={{
              m: 0,
              p: 0,
              listStyle: 'none',
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--space-4)',
              maxHeight: 280,
              overflowY: 'auto'
            }}
          >
            {tasks.map((task) => (
              <Box
                component="li"
                key={task.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-8)',
                  minHeight: 36,
                  pl: 'var(--space-8)',
                  borderRadius: 'var(--radius-sm)',
                  bgcolor: 'var(--color-bg-surface)'
                }}
              >
                <Box
                  title={task.name}
                  sx={{
                    ...textSx,
                    flex: '1 0 0',
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    fontSize: 'var(--font-size-14)',
                    fontWeight: 'var(--font-weight-regular)'
                  }}
                >
                  {task.name}
                </Box>
                {statusIcon(task.status)}
                <IconButton icon="close" variant="ghost" aria-label={`Remove ${task.name}`} onClick={() => removeTaskById(task.id)} />
              </Box>
            ))}
          </Box>
        )}
      </Popover>
    </>
  )
}
